import { useEffect, useRef, useState } from 'react';
import { GearSix } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Slider } from '~/components/ui/slider';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useProBuilderStatus, type ProBuilderConsentMode, type ProBuilderStatus } from '~/hooks/queries/use-pro-builder';
import { useSaveProBuilderAutoCompact, useSaveProBuilderConsentMode } from '~/hooks/mutations/use-pro-builder';
import { ProBuilderConsentModeChoice } from './pro-builder-consent-mode-choice';
import {
  AUTO_COMPACT_MAX_POSITION,
  autoCompactHeading,
  autoCompactPanelState,
  autoCompactPosition,
  autoCompactValue,
} from './auto-compact';
import {
  PRO_BUILDER_AUTO_COMPACT_LEFT,
  PRO_BUILDER_AUTO_COMPACT_LOAD_FAILED,
  PRO_BUILDER_AUTO_COMPACT_LOADING,
  PRO_BUILDER_AUTO_COMPACT_RESTART_BUTTON,
  PRO_BUILDER_AUTO_COMPACT_RESTART_NOTE,
  PRO_BUILDER_AUTO_COMPACT_RIGHT,
  PRO_BUILDER_AUTO_COMPACT_SAVE_FAILED,
  PRO_BUILDER_AUTO_COMPACT_SLIDER_LABEL,
  PRO_BUILDER_CONSENT_MODE_LOAD_FAILED,
  PRO_BUILDER_CONSENT_MODE_OTHERS_NOTE,
  PRO_BUILDER_CONSENT_MODE_SAVE_FAILED,
  PRO_BUILDER_CONSENT_MODE_SETTINGS_HEADING,
  PRO_BUILDER_SETTINGS_LABEL,
} from './pro-builder-copy';
import { useProBuilderSessionStore } from './pro-builder-session-store';

/**
 * FRONT-3228 — the popover body: heading, slider with its two edge labels, and
 * (only while a restart is owed) the restart row. Props only, so the header's
 * connected wrapper below is the one place that touches the status read, the
 * save and the session store.
 */
export function ProBuilderClaudeSettingsPanel({
  position,
  onPositionChange,
  onCommit,
  sliderDisabled,
  notice,
  restartOwed,
  onRestart,
}: {
  position: number;
  onPositionChange: (position: number) => void;
  onCommit: (position: number) => void;
  sliderDisabled: boolean;
  notice: 'loading' | 'error' | null;
  restartOwed: boolean;
  onRestart: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 p-1.5">
      {/* While nothing is loaded the heading would name a value nobody saved. */}
      {notice === null && (
        <h3 data-testid="pro-builder-auto-compact-heading" className="text-sm font-medium text-foreground">
          {autoCompactHeading(position)}
        </h3>
      )}
      <div className="flex flex-col gap-1.5">
        <Slider
          min={0}
          max={AUTO_COMPACT_MAX_POSITION}
          step={1}
          value={[position]}
          disabled={sliderDisabled}
          thumbProps={{
            'aria-label': PRO_BUILDER_AUTO_COMPACT_SLIDER_LABEL,
            'aria-valuetext': autoCompactValue(position) === null ? PRO_BUILDER_AUTO_COMPACT_RIGHT : `${autoCompactValue(position)}%`,
          }}
          data-testid="pro-builder-auto-compact-slider"
          onValueChange={(v) => onPositionChange(v[0] ?? position)}
          onValueCommit={(v) => onCommit(v[0] ?? position)}
        />
        <div className="flex justify-between text-xs text-muted-foreground" aria-hidden>
          <span>{PRO_BUILDER_AUTO_COMPACT_LEFT}</span>
          <span>{PRO_BUILDER_AUTO_COMPACT_RIGHT}</span>
        </div>
      </div>
      {notice !== null && (
        <p data-testid="pro-builder-auto-compact-notice" className="text-xs text-muted-foreground">
          {notice === 'error' ? PRO_BUILDER_AUTO_COMPACT_LOAD_FAILED : PRO_BUILDER_AUTO_COMPACT_LOADING}
        </p>
      )}
      {restartOwed && (
        <div data-testid="pro-builder-auto-compact-restart-row" className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{PRO_BUILDER_AUTO_COMPACT_RESTART_NOTE}</span>
          <Button variant="outline" size="xs" data-testid="pro-builder-auto-compact-restart" onClick={onRestart}>
            {PRO_BUILDER_AUTO_COMPACT_RESTART_BUTTON}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The header's gear. The tooltip composes the raw primitives around the popover
 * trigger (never `Tip`, which would swallow the trigger's cloned props). The
 * slider starts from the saved value each time the popover opens; moving it
 * updates the heading from local state, and only a commit saves. A failed save
 * puts the slider back on the last saved value.
 */
export function ProBuilderClaudeSettings() {
  const status = useProBuilderStatus();
  const save = useSaveProBuilderAutoCompact();
  const startedWith = useProBuilderSessionStore((s) => s.session.startedWith);
  const dispatch = useProBuilderSessionStore((s) => s.dispatch);
  const saved = status.data?.autoCompactPct ?? null;
  const panelState = autoCompactPanelState({
    hasData: status.data !== undefined,
    isError: status.isError,
    startedWith,
    saved,
  });
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(() => autoCompactPosition(saved));
  const hasData = status.data !== undefined;
  // Whether the slider has been synced from a LOADED saved value yet. Opened
  // while the status read was still loading, the slider sat locked on Default;
  // the first data must still move it to the saved value, even while open.
  const syncedWithData = useRef(hasData);
  // While closed the slider mirrors the saved value, so the next open starts from it.
  useEffect(() => {
    if (!open || !syncedWithData.current) setPosition(autoCompactPosition(saved));
    if (hasData) syncedWithData.current = true;
  }, [open, saved, hasData]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={PRO_BUILDER_SETTINGS_LABEL}
              data-testid="pro-builder-settings-menu"
              className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <GearSix size={18} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{PRO_BUILDER_SETTINGS_LABEL}</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-72" data-testid="pro-builder-settings-popover">
        <ProBuilderClaudeSettingsPanel
          position={position}
          onPositionChange={setPosition}
          onCommit={(p) =>
            save.mutate(autoCompactValue(p), {
              onError: () => {
                setPosition(autoCompactPosition(savedRef.current));
                toast.error(PRO_BUILDER_AUTO_COMPACT_SAVE_FAILED);
              },
            })
          }
          sliderDisabled={panelState.sliderDisabled}
          notice={panelState.notice}
          restartOwed={panelState.showRestartRow}
          onRestart={() => dispatch({ type: 'restart' })}
        />
        <ProBuilderConsentModeSettings />
      </PopoverContent>
    </Popover>
  );
}

/**
 * FRONT-3230 — the gear menu's copy of the card's "Next time" choice, shown
 * only when Claude Code gets the Eddy connection at all (no connection, no
 * cards, nothing to choose). Shows the saved setting, or the pick while its
 * save is in flight; a failed save says so and the choice falls back to the
 * saved value by itself. A status read that failed says so instead.
 */
export function ProBuilderConsentModeSettings() {
  const status = useProBuilderStatus();
  const save = useSaveProBuilderConsentMode();
  return (
    <ProBuilderConsentModeSettingsSection
      isError={status.isError}
      status={status.data}
      saving={save.isPending ? save.variables : undefined}
      onChange={(mode) => save.mutate(mode, { onError: () => toast.error(PRO_BUILDER_CONSENT_MODE_SAVE_FAILED) })}
    />
  );
}

/**
 * Props only: which of the three the gear menu shows. A status read in error says it could not load the setting
 * (keyed off the query's error flag, so a failure never reads as "still loading" or as "switched off"); a loaded
 * status shows the setting only while the EDDY connection is on; a read still in flight shows nothing.
 */
export function ProBuilderConsentModeSettingsSection({
  isError,
  status,
  saving,
  onChange,
}: {
  isError: boolean;
  status: Pick<ProBuilderStatus, 'eddyAgents' | 'consentMode'> | undefined;
  saving: ProBuilderConsentMode | undefined;
  onChange: (mode: ProBuilderConsentMode) => void;
}) {
  if (isError) {
    return (
      <p
        data-testid="pro-builder-consent-mode-setting-error"
        className="mt-3 border-t border-border p-1.5 pt-3 text-xs text-muted-foreground"
      >
        {PRO_BUILDER_CONSENT_MODE_LOAD_FAILED}
      </p>
    );
  }
  // Shown only once the status has loaded, so there is always a saved value to select.
  if (!status || !showsConsentModeSettings(status)) return null;
  return <ProBuilderConsentModeSettingsPanel value={consentModeSettingsValue(status, saving)} onChange={onChange} />;
}

/** Whether the gear menu shows the approval setting: only once the status says the Eddy connection is on (an older server never says so). */
export function showsConsentModeSettings(status: Pick<ProBuilderStatus, 'eddyAgents'> | undefined): boolean {
  return status?.eddyAgents === true;
}

/** The value the gear menu shows: a save in flight, else the saved setting (an older server's status has none: ask every time). */
export function consentModeSettingsValue(
  status: Pick<ProBuilderStatus, 'consentMode'>,
  saving: ProBuilderConsentMode | undefined,
): ProBuilderConsentMode {
  return saving ?? status.consentMode ?? 'ask-all';
}

/** Props only, like the compaction panel above: the radio group and the line saying what always asks. */
export function ProBuilderConsentModeSettingsPanel({
  value,
  onChange,
}: {
  value: ProBuilderConsentMode;
  onChange: (mode: ProBuilderConsentMode) => void;
}) {
  return (
    <div className="mt-3 flex flex-col gap-2 border-t border-border p-1.5 pt-3">
      <ProBuilderConsentModeChoice
        heading={PRO_BUILDER_CONSENT_MODE_SETTINGS_HEADING}
        value={value}
        onChange={onChange}
        testId="pro-builder-consent-mode-setting"
      />
      <p data-testid="pro-builder-consent-mode-setting-note" className="text-xs text-muted-foreground">
        {PRO_BUILDER_CONSENT_MODE_OTHERS_NOTE}
      </p>
    </div>
  );
}
